import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RefreshCw, Upload, Image as ImageIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { validateFileUpload } from "@/lib/validation";
import { StorageImage } from "@/components/StorageImage";
import type { PackageWithSchedule } from "@/types/database";
import { toast } from "sonner";

interface ResubmitPackageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pkg: PackageWithSchedule | null;
  userId: string;
  onSuccess: () => void;
}

const RETAILER_LOCATION_MAP: Record<string, string> = {
  "amazon-kohls": "K",
  "amazon-ups": "U",
  "amazon-fedex": "F",
  "amazon-staples": "S",
  "amazon-wholefoods": "W",
};

export const ResubmitPackageDialog = ({
  open,
  onOpenChange,
  pkg,
  userId,
  onSuccess,
}: ResubmitPackageDialogProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [retailer, setRetailer] = useState("");
  const [size, setSize] = useState("");
  const [locationCode, setLocationCode] = useState("");
  const [notes, setNotes] = useState("");
  const [returnByDate, setReturnByDate] = useState("");
  const [printQrNeeded, setPrintQrNeeded] = useState(false);

  // File state
  const [newQrCodeFile, setNewQrCodeFile] = useState<File | null>(null);
  const [newPackagePhotoFile, setNewPackagePhotoFile] = useState<File | null>(null);
  const [keepExistingQr, setKeepExistingQr] = useState(true);
  const [keepExistingPhoto, setKeepExistingPhoto] = useState(true);

  // Pre-fill from package data
  useEffect(() => {
    if (pkg && open) {
      setRetailer(pkg.retailer || "");
      setSize(pkg.size || "");
      setLocationCode(pkg.location_code || "");
      setNotes(pkg.notes || "");
      setReturnByDate(pkg.return_by_date || "");
      setPrintQrNeeded(pkg.print_qr_needed || false);
      setKeepExistingQr(true);
      setKeepExistingPhoto(true);
      setNewQrCodeFile(null);
      setNewPackagePhotoFile(null);
    }
  }, [pkg, open]);

  const uploadFile = async (file: File, bucket: string) => {
    const validation = validateFileUpload(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }
    const fileExt = file.name.split(".").pop();
    const fileName = `${userId}/${Math.random()}.${fileExt}`;
    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(fileName, file);
    if (uploadError) throw uploadError;
    return fileName;
  };

  const handleSubmit = async () => {
    if (!pkg) return;

    // Validation
    if (!retailer || !size || !locationCode) {
      toast.error("Please fill in all required fields");
      return;
    }

    // QR code validation
    const hasQr = printQrNeeded || (keepExistingQr && pkg.qr_code_url) || newQrCodeFile;
    if (!hasQr) {
      toast.error("Please upload a QR code or select 'Print QR for me'");
      return;
    }

    // Package photo validation
    const hasPhoto = (keepExistingPhoto && pkg.package_photo_url) || newPackagePhotoFile || printQrNeeded;
    if (!hasPhoto) {
      toast.error("Please upload a package photo");
      return;
    }

    setSubmitting(true);
    try {
      let qrCodeUrl = keepExistingQr ? pkg.qr_code_url : null;
      let packagePhotoUrl = keepExistingPhoto ? pkg.package_photo_url : null;

      if (newQrCodeFile) {
        qrCodeUrl = await uploadFile(newQrCodeFile, "qr-codes");
      }
      if (newPackagePhotoFile) {
        packagePhotoUrl = await uploadFile(newPackagePhotoFile, "package-photos");
      }

      // Create resubmitted package
      const { error: insertError } = await supabase
        .from("packages")
        .insert({
          user_id: userId,
          retailer,
          size,
          city: pkg.city,
          notes: notes || null,
          location_code: locationCode,
          print_qr_needed: printQrNeeded,
          qr_code_url: qrCodeUrl,
          package_photo_url: packagePhotoUrl,
          return_by_date: returnByDate || null,
          status: "pending",
          approval_status: "pending",
          resubmitted_from_package_id: pkg.id,
        });

      if (insertError) throw insertError;

      // Mark original as resubmitted
      const { error: updateError } = await supabase
        .from("packages")
        .update({
          was_resubmitted: true,
          status: "resubmitted",
        })
        .eq("id", pkg.id);

      if (updateError) throw updateError;

      toast.success("Package resubmitted successfully");
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error("Error resubmitting package:", error);
      }
      toast.error(error.message || "Failed to resubmit package");
    } finally {
      setSubmitting(false);
    }
  };

  if (!pkg) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-background">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-primary" />
            Edit & Resubmit Package
          </DialogTitle>
          <DialogDescription>
            Review and update your package details before resubmitting.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Retailer */}
          <div className="space-y-2">
            <Label>Return Location</Label>
            <Select
              value={retailer}
              onValueChange={(value) => {
                setRetailer(value);
                setLocationCode(RETAILER_LOCATION_MAP[value] || "");
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="amazon-kohls">Kohl's</SelectItem>
                <SelectItem value="amazon-ups">UPS</SelectItem>
                <SelectItem value="amazon-fedex">FedEx</SelectItem>
                <SelectItem value="amazon-staples">Staples</SelectItem>
                <SelectItem value="amazon-wholefoods">Whole Foods</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Location Code */}
          <div className="space-y-2">
            <Label>Location Code</Label>
            <Input
              value={locationCode}
              onChange={(e) => setLocationCode(e.target.value.toUpperCase())}
              maxLength={1}
              pattern="[KUFSW]"
              readOnly
            />
          </div>

          {/* Size */}
          <div className="space-y-2">
            <Label>Package Size</Label>
            <Select value={size} onValueChange={setSize}>
              <SelectTrigger>
                <SelectValue placeholder="Select size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Small (shoe box)</SelectItem>
                <SelectItem value="medium">Medium (standard box)</SelectItem>
                <SelectItem value="large">Large (suitcase)</SelectItem>
                <SelectItem value="oversized">Oversized</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Return By Date */}
          <div className="space-y-2">
            <Label>Return By Date (Optional)</Label>
            <Input
              type="date"
              value={returnByDate}
              onChange={(e) => setReturnByDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
            />
          </div>

          {/* Package Photo */}
          <div className="space-y-2">
            <Label>Package Photo</Label>
            {keepExistingPhoto && pkg.package_photo_url && (
              <div className="space-y-2">
                <div className="border rounded-lg p-2 bg-muted/30">
                  <StorageImage
                    bucket="package-photos"
                    storedValue={pkg.package_photo_url}
                    alt="Current Package Photo"
                    className="w-full max-h-32 object-contain rounded"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setKeepExistingPhoto(false)}
                  className="w-full"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Replace Photo
                </Button>
              </div>
            )}
            {(!keepExistingPhoto || !pkg.package_photo_url) && (
              <label>
                <div className="border-2 border-dashed border-input rounded-lg p-4 cursor-pointer hover:border-primary transition-colors">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <ImageIcon className="w-4 h-4" />
                    {newPackagePhotoFile ? newPackagePhotoFile.name : "Upload package photo"}
                  </div>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setNewPackagePhotoFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* QR Code */}
          <div className="space-y-2">
            <Label>Return QR Code</Label>
            {keepExistingQr && pkg.qr_code_url && !printQrNeeded && (
              <div className="space-y-2">
                <div className="border rounded-lg p-2 bg-muted/30">
                  <StorageImage
                    bucket="qr-codes"
                    storedValue={pkg.qr_code_url}
                    alt="Current QR Code"
                    className="w-full max-h-32 object-contain rounded"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setKeepExistingQr(false)}
                  className="w-full"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Replace QR Code
                </Button>
              </div>
            )}
            {(!keepExistingQr || !pkg.qr_code_url) && !printQrNeeded && (
              <label>
                <div className="border-2 border-dashed border-input rounded-lg p-4 cursor-pointer hover:border-primary transition-colors">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Upload className="w-4 h-4" />
                    {newQrCodeFile ? newQrCodeFile.name : "Upload QR code image"}
                  </div>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setNewQrCodeFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>
            )}
            {printQrNeeded && (
              <p className="text-sm text-muted-foreground">Returnify will print the QR code for you.</p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Additional Notes (Optional)</Label>
            <Input
              placeholder="Any special instructions?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={200}
            />
            <p className="text-xs text-muted-foreground">
              {notes.length}/200 characters
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            <RefreshCw className={`w-4 h-4 mr-2 ${submitting ? "animate-spin" : ""}`} />
            {submitting ? "Resubmitting..." : "Resubmit Package"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
